import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  downloadAgentCabinetReconciliationExcel,
  getAgentCabinetReconciliation,
} from "../../api/agentCabinet";
import i18n from "../../i18n";

const formatUah = (value) => {
  const amount = Number(value || 0).toLocaleString("uk-UA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return i18n.t("units.uahAmount", { amount });
};

const AgentSelfReconciliationPage = () => {
  const { t } = useTranslation("common");
  const [monthValue, setMonthValue] = useState(dayjs().format("YYYY-MM"));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const loadReport = useCallback(async () => {
    if (!monthValue) return;
    const [y, m] = monthValue.split("-").map(Number);
    setLoading(true);
    setError("");
    try {
      const data = await getAgentCabinetReconciliation({ year: y, month: m });
      setReport(data);
    } catch (e) {
      setError(t("agent.loadError"));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [monthValue, t]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    if (!monthValue) return;
    const [y, m] = monthValue.split("-").map(Number);
    setExporting(true);
    try {
      const resp = await downloadAgentCabinetReconciliationExcel({
        year: y,
        month: m,
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `agent-reconciliation-${monthValue}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(t("agent.exportError"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <MuiLink
        component={RouterLink}
        to="/agent"
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {t("agent.dashboardTitle")}
      </MuiLink>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("agent.reconciliation")}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          type="month"
          size="small"
          label={t("admin.reconciliationMonth")}
          value={monthValue}
          onChange={(e) => setMonthValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || loading}
        >
          {t("admin.exportExcel")}
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="body1">
              {t("agent.commission")}: {report.agent?.commission_percent}%
            </Typography>
            <Typography variant="body1">
              {t("agent.passengers")}: {report.totals?.passengers ?? 0}
            </Typography>
            <Typography variant="body1">
              {t("agent.dueToYou")}: {formatUah(report.totals?.due_to_agent_uah)}
            </Typography>
            <Typography variant="body1">
              {t("agent.dueFromYou")}:{" "}
              {formatUah(report.totals?.due_from_agent_uah)}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {t("agent.net")}: {formatUah(report.totals?.net_uah)}
            </Typography>
          </Paper>
          {(report.rows || []).length === 0 ? (
            <Typography color="text.secondary">
              {t("admin.reconciliationEmpty")}
            </Typography>
          ) : (
            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("agent.colNumber")}</TableCell>
                    <TableCell>{t("agent.colPassenger")}</TableCell>
                    <TableCell align="right">{t("agent.colTicket")}</TableCell>
                    <TableCell align="right">{t("agent.colCommission")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((row, idx) => (
                    <TableRow key={`${row.booking_number}-${idx}`}>
                      <TableCell>{row.booking_number}</TableCell>
                      <TableCell>
                        {row.surname} {row.name}
                      </TableCell>
                      <TableCell align="right">
                        {formatUah(row.ticket_uah)}
                      </TableCell>
                      <TableCell align="right">
                        {formatUah(row.commission_uah)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      ) : null}
    </Container>
  );
};

export default AgentSelfReconciliationPage;
